import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { debounce } from 'lodash';
import useApi from '../../hook/useAPI';
import { TIME_LOGS, USER_MANAGEMENT } from '../../utils/const';
import { TIME_LOG_ACTION, TIME_LOG_ACTION_LABEL } from '../../constants/timeLog';
import { convert } from '../../utils/moment';
import { formatDeviceLocation, formatUserLocation } from '../../utils/timeLogDisplay';

const USER_SEARCH_LIMIT = 15;
const USER_SEARCH_MIN_LENGTH = 2;

const defaultFromDate = () => {
  const date = new Date();
  date.setDate(date.getDate() - 30);
  return date.toISOString().slice(0, 10);
};

const formatDuration = (minutes) => {
  if (minutes == null) return '—';
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (hours > 0) return `${hours}h ${mins}m`;
  return `${mins}m`;
};

const formatUserName = (user) => {
  if (!user) return 'Unknown user';
  const name = `${user.firstname || ''} ${user.lastname || ''}`.trim();
  return name || user.email || 'Unknown user';
};

const ShiftStatusBadge = ({ onShift }) => (
  <span className={`time-tracking-status-badge ${onShift ? 'time-tracking-status-badge--on' : 'time-tracking-status-badge--off'}`}>
    {onShift ? 'On shift' : 'Off shift'}
  </span>
);

const ActionBadge = ({ action }) => {
  const actionClass = {
    [TIME_LOG_ACTION.IN]: 'time-tracking-action-badge--in',
    [TIME_LOG_ACTION.RESUME]: 'time-tracking-action-badge--resume',
    [TIME_LOG_ACTION.PAUSE]: 'time-tracking-action-badge--pause',
    [TIME_LOG_ACTION.OUT]: 'time-tracking-action-badge--out',
  }[action] || 'time-tracking-action-badge--in';

  return (
    <span className={`time-tracking-action-badge ${actionClass}`}>
      {TIME_LOG_ACTION_LABEL[action] || action}
    </span>
  );
};

function AdminTimeTracking() {
  const { getData } = useApi();
  const [searchParams, setSearchParams] = useSearchParams();

  const [selectedUserId, setSelectedUserId] = useState(() => searchParams.get('userId') || '');
  const [selectedUser, setSelectedUser] = useState(null);
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [userSearchResults, setUserSearchResults] = useState([]);
  const [userSearchLoading, setUserSearchLoading] = useState(false);
  const [draftFromDate, setDraftFromDate] = useState(defaultFromDate);
  const [draftToDate, setDraftToDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [draftActionFilter, setDraftActionFilter] = useState('');
  const [draftDeviceFilter, setDraftDeviceFilter] = useState('');
  const [appliedFromDate, setAppliedFromDate] = useState(defaultFromDate);
  const [appliedToDate, setAppliedToDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [appliedActionFilter, setAppliedActionFilter] = useState('');
  const [appliedDeviceFilter, setAppliedDeviceFilter] = useState('');
  const [deviceFilterOptions, setDeviceFilterOptions] = useState([]);
  const [summaryRows, setSummaryRows] = useState([]);
  const [logs, setLogs] = useState([]);
  const [userSummary, setUserSummary] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const userId = searchParams.get('userId') || '';
    setSelectedUserId(userId);
    if (!userId) {
      setSelectedUser(null);
    }
  }, [searchParams]);

  useEffect(() => {
    const fetchSelectedUser = async () => {
      if (!selectedUserId) {
        return;
      }
      if (selectedUser?._id === selectedUserId) {
        return;
      }

      try {
        const response = await getData(USER_MANAGEMENT.GET_SINGLE_USER, selectedUserId);
        if (response?.users?.user) {
          setSelectedUser(response.users.user);
        }
      } catch (err) {
        console.error('Failed to load selected user', err);
      }
    };

    fetchSelectedUser();
  }, [getData, selectedUser, selectedUserId]);

  const searchUsers = useCallback(
    debounce(async (query) => {
      const trimmed = query.trim();
      if (trimmed.length < USER_SEARCH_MIN_LENGTH) {
        setUserSearchResults([]);
        setUserSearchLoading(false);
        return;
      }

      setUserSearchLoading(true);
      try {
        const response = await getData(
          `${USER_MANAGEMENT.GET_ALL_USER}?page=1&limit=${USER_SEARCH_LIMIT}&search=${encodeURIComponent(trimmed)}`
        );
        setUserSearchResults(response?.users || []);
      } catch (err) {
        console.error('Failed to search users', err);
        setUserSearchResults([]);
      } finally {
        setUserSearchLoading(false);
      }
    }, 400),
    [getData]
  );

  useEffect(() => {
    searchUsers(userSearchQuery);
    return () => searchUsers.cancel();
  }, [searchUsers, userSearchQuery]);

  const buildQueryParams = useCallback(() => {
    const params = new URLSearchParams();
    if (appliedFromDate) {
      params.set('from', new Date(`${appliedFromDate}T00:00:00`).toISOString());
    }
    if (appliedToDate) {
      params.set('to', new Date(`${appliedToDate}T23:59:59`).toISOString());
    }
    if (appliedActionFilter) {
      params.set('action', appliedActionFilter);
    }
    if (appliedDeviceFilter) {
      params.set('deviceId', appliedDeviceFilter);
    }
    return params;
  }, [appliedActionFilter, appliedDeviceFilter, appliedFromDate, appliedToDate]);

  const fetchTimeData = useCallback(async () => {
    setLoading(true);
    try {
      const params = buildQueryParams();

      if (selectedUserId) {
        const logParams = new URLSearchParams(params);
        logParams.set('page', '1');
        logParams.set('limit', '100');

        const [logsResponse, reportResponse] = await Promise.all([
          getData(`${TIME_LOGS.USER_LOGS(selectedUserId)}?${logParams.toString()}`, null, false),
          getData(`${TIME_LOGS.REPORT}?${params.toString()}&userId=${selectedUserId}`, null, false),
        ]);

        setLogs(logsResponse?.status === 200 ? logsResponse.data || [] : []);
        setUserSummary(reportResponse?.data?.[0] || null);
        setSummaryRows([]);
      } else {
        const reportResponse = await getData(`${TIME_LOGS.REPORT}?${params.toString()}`, null, false);
        setSummaryRows(reportResponse?.status === 200 ? reportResponse.data || [] : []);
        setLogs([]);
        setUserSummary(null);
      }
    } catch (err) {
      console.error('Failed to load time tracking data', err);
    } finally {
      setLoading(false);
    }
  }, [buildQueryParams, getData, selectedUserId]);

  useEffect(() => {
    fetchTimeData();
  }, [fetchTimeData, selectedUserId, appliedFromDate, appliedToDate, appliedActionFilter, appliedDeviceFilter]);

  useEffect(() => {
    if (!selectedUserId || appliedDeviceFilter) {
      return;
    }
    const map = new Map();
    logs.forEach((log) => {
      if (log.deviceId) {
        map.set(String(log.deviceId), log.deviceName || 'Device');
      }
    });
    if (map.size > 0) {
      setDeviceFilterOptions(
        Array.from(map, ([id, name]) => ({ id, name }))
      );
    }
  }, [appliedDeviceFilter, logs, selectedUserId]);

  const applyFilters = () => {
    setAppliedFromDate(draftFromDate);
    setAppliedToDate(draftToDate);
    setAppliedActionFilter(draftActionFilter);
    setAppliedDeviceFilter(draftDeviceFilter);
  };

  const clearUserSelection = () => {
    setSelectedUserId('');
    setSelectedUser(null);
    setUserSearchQuery('');
    setUserSearchResults([]);
    setSearchParams({});
  };

  const selectUser = (user) => {
    setSelectedUserId(user._id);
    setSelectedUser(user);
    setUserSearchQuery('');
    setUserSearchResults([]);
    setSearchParams({ userId: user._id });
  };

  const openUserLogs = (userId, user = null) => {
    setSelectedUserId(userId);
    if (user) {
      setSelectedUser(user);
    }
    setUserSearchQuery('');
    setUserSearchResults([]);
    setSearchParams({ userId });
  };

  const displayUser = selectedUser || userSummary?.user;
  const resultCountLabel = selectedUserId
    ? `${logs.length} ${logs.length === 1 ? 'log' : 'logs'}`
    : `${summaryRows.length} ${summaryRows.length === 1 ? 'user' : 'users'}`;

  return (
    <div className="dashboard time-tracking-page admin-time-tracking">
      <div className="card card-table-wrap">
        <div className="card-body">
          <div className="admin-time-tracking-header">
            <div className="admin-time-tracking-header__copy">
              {selectedUserId && (
                <button
                  type="button"
                  className="admin-time-tracking-back"
                  onClick={clearUserSelection}
                >
                  ← All users
                </button>
              )}
              <h5 className="admin-time-tracking-title">Time Tracking</h5>
              <p className="admin-time-tracking-subtitle">
                {selectedUserId
                  ? `Work clock history for ${formatUserName(displayUser)}.`
                  : 'Review work clock logs by user and date range.'}
              </p>
            </div>
            {!loading && (logs.length > 0 || summaryRows.length > 0) && (
              <span className="admin-time-tracking-result-count">{resultCountLabel}</span>
            )}
          </div>

          <div className="admin-time-tracking-filters">
            <div className="admin-time-tracking-filter admin-time-tracking-user-filter">
              <span>User</span>
              {selectedUserId ? (
                <div className="admin-time-tracking-selected-user">
                  <span className="admin-time-tracking-selected-user-label">
                    {formatUserName(displayUser)}
                    {displayUser?.email ? ` (${displayUser.email})` : ''}
                  </span>
                  <button
                    type="button"
                    className="admin-time-tracking-clear-user"
                    onClick={clearUserSelection}
                  >
                    Change
                  </button>
                </div>
              ) : (
                <div className="admin-time-tracking-user-search">
                  <input
                    type="search"
                    className="form-control"
                    value={userSearchQuery}
                    onChange={(e) => setUserSearchQuery(e.target.value)}
                    placeholder="Search name or email…"
                    autoComplete="off"
                  />
                  {userSearchLoading && (
                    <div className="admin-time-tracking-user-search-hint">Searching…</div>
                  )}
                  {!userSearchLoading && userSearchQuery.trim().length >= USER_SEARCH_MIN_LENGTH
                    && userSearchResults.length === 0 && (
                    <div className="admin-time-tracking-user-search-hint">No users found.</div>
                  )}
                  {userSearchResults.length > 0 && (
                    <ul className="admin-time-tracking-user-search-results">
                      {userSearchResults.map((user) => (
                        <li key={user._id}>
                          <button type="button" onClick={() => selectUser(user)}>
                            {formatUserName(user)}
                            {user.email ? ` (${user.email})` : ''}
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>
            <label className="admin-time-tracking-filter">
              <span>From</span>
              <input
                type="date"
                className="form-control"
                value={draftFromDate}
                onChange={(e) => setDraftFromDate(e.target.value)}
              />
            </label>
            <label className="admin-time-tracking-filter">
              <span>To</span>
              <input
                type="date"
                className="form-control"
                value={draftToDate}
                onChange={(e) => setDraftToDate(e.target.value)}
              />
            </label>
            <label className="admin-time-tracking-filter">
              <span>Action</span>
              <select
                className="form-select"
                value={draftActionFilter}
                onChange={(e) => setDraftActionFilter(e.target.value)}
              >
                <option value="">All actions</option>
                <option value={TIME_LOG_ACTION.IN}>{TIME_LOG_ACTION_LABEL[TIME_LOG_ACTION.IN]}</option>
                <option value={TIME_LOG_ACTION.PAUSE}>{TIME_LOG_ACTION_LABEL[TIME_LOG_ACTION.PAUSE]}</option>
                <option value={TIME_LOG_ACTION.RESUME}>{TIME_LOG_ACTION_LABEL[TIME_LOG_ACTION.RESUME]}</option>
                <option value={TIME_LOG_ACTION.OUT}>{TIME_LOG_ACTION_LABEL[TIME_LOG_ACTION.OUT]}</option>
              </select>
            </label>
            {selectedUserId && deviceFilterOptions.length > 0 && (
              <label className="admin-time-tracking-filter">
                <span>Device</span>
                <select
                  className="form-select"
                  value={draftDeviceFilter}
                  onChange={(e) => setDraftDeviceFilter(e.target.value)}
                >
                  <option value="">All devices</option>
                  {deviceFilterOptions.map((device) => (
                    <option key={device.id} value={device.id}>
                      {device.name}
                    </option>
                  ))}
                </select>
              </label>
            )}
            <button
              type="button"
              className="btn btn-primary admin-time-tracking-apply"
              onClick={applyFilters}
              disabled={loading}
            >
              {loading ? 'Loading…' : 'Apply'}
            </button>
          </div>

          {selectedUserId && userSummary && (
            <div className="admin-time-tracking-stats">
              <div className="admin-time-tracking-stat">
                <span className="label">User</span>
                <strong>{formatUserName(displayUser)}</strong>
              </div>
              <div className="admin-time-tracking-stat">
                <span className="label">Shifts</span>
                <strong>{userSummary.shiftCount}</strong>
              </div>
              <div className="admin-time-tracking-stat">
                <span className="label">Total hours</span>
                <strong>{userSummary.totalHours}</strong>
              </div>
              <div className="admin-time-tracking-stat">
                <span className="label">Status</span>
                <ShiftStatusBadge onShift={userSummary.hasOpenShift} />
              </div>
            </div>
          )}

          <div className="table-responsive admin-time-tracking-table-wrap">
            {selectedUserId ? (
              <table className="table admin-time-tracking-table">
                <thead>
                  <tr>
                    <th>Date / Time</th>
                    <th>Action</th>
                    <th>Device</th>
                    <th>Name</th>
                    <th>Your location</th>
                    <th>Device location</th>
                    <th>Duration</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="admin-time-tracking-empty">
                        {loading ? 'Loading…' : 'No logs for this user in the selected period.'}
                      </td>
                    </tr>
                  ) : (
                    logs.map((log) => (
                      <tr key={log._id}>
                        <td>{convert(log.timestamp, 'DD MMM YYYY HH:mm:ss')}</td>
                        <td>
                          <ActionBadge action={log.action} />
                        </td>
                        <td>{log.deviceName || '—'}</td>
                        <td>{log.notes || '—'}</td>
                        <td>{formatUserLocation(log.geofenceData)}</td>
                        <td>{formatDeviceLocation(log.deviceSnapshot)}</td>
                        <td>
                          {log.action === TIME_LOG_ACTION.OUT
                            ? formatDuration(log.durationMinutes)
                            : '—'}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            ) : (
              <table className="table admin-time-tracking-table">
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Email</th>
                    <th>Shifts</th>
                    <th>Total hours</th>
                    <th>Status</th>
                    <th className="text-end">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {summaryRows.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="admin-time-tracking-empty">
                        {loading ? 'Loading…' : 'No time logs in the selected period.'}
                      </td>
                    </tr>
                  ) : (
                    summaryRows.map((row) => (
                      <tr key={row.userId}>
                        <td>{formatUserName(row.user)}</td>
                        <td>{row.user?.email || '—'}</td>
                        <td>{row.shiftCount}</td>
                        <td>{row.totalHours}</td>
                        <td>
                          <ShiftStatusBadge onShift={row.hasOpenShift} />
                        </td>
                        <td className="text-end">
                          <button
                            type="button"
                            className="admin-time-tracking-view-logs"
                            onClick={() => openUserLogs(row.userId, row.user)}
                          >
                            View logs
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default AdminTimeTracking;
